/**
 * Browser-side error reporting. See SPEC/2619.
 *
 * The endpoint is the ordinary authenticated API, so the user id in CloudWatch is
 * the one the gateway's authorizer verified rather than one the page claimed.
 */

const DEFAULT_ENDPOINT = "/api/v1/client-reports";
const DEFAULT_MAX_PER_SESSION = 20;
const MAX_STACK_FRAMES = 30;

/**
 * Hard ceiling, in BYTES, for a serialized report body. 6 KB sits far inside the
 * 64 KB budget a browser gives all in-flight `keepalive` requests together — the
 * budget that decides whether a report sent during unload leaves the machine —
 * and the backend drops anything over 16 KiB outright.
 */
export const MAX_BODY_BYTES = 6 * 1024;

/**
 * Returns the current access token, or null when there is none. SYNCHRONOUS on
 * purpose: reportError runs on the unload path, where there is nothing left to
 * await.
 */
export type TokenGetter = () => string | null;

let installed = false;
let sent = 0;
let maxPerSession = DEFAULT_MAX_PER_SESSION;
let endpoint = DEFAULT_ENDPOINT;
let getToken: TokenGetter | null = null;
let extraHeaders: Record<string, string> = {};
const seen = new Set<string>();
/**
 * Fingerprints already given a second chance after a 401. One each: a token that
 * stays bad must not turn a failed send into an unbounded retry loop.
 */
const released = new Set<string>();
let teardown: Array<() => void> = [];

export interface ReporterOptions {
  endpoint?: string;
  maxPerSession?: number;
  getToken?: TokenGetter;
  /**
   * Extra headers attached to every report. The lean local stack has no API
   * Gateway, so the SPA must supply the identity headers the backend reads;
   * deployed, the gateway overwrites them from the authorizer. The caller
   * decides what goes here — this package knows nothing about gateway claims.
   */
  extraHeaders?: Record<string, string>;
}

/**
 * Registers the token reader after installReporter has already run.
 *
 * installReporter is called at module scope, before React mounts, so no auth
 * provider exists yet. The getter is read through a function on every send, never
 * captured, because the real one only arrives after the first render.
 */
export function setTokenGetter(next: TokenGetter | null): void {
  getToken = next;
}

/** Test-only: resets module state (and listeners) between cases. */
export function __resetForTests(): void {
  teardown.forEach((off) => off());
  teardown = [];
  installed = false;
  sent = 0;
  maxPerSession = DEFAULT_MAX_PER_SESSION;
  endpoint = DEFAULT_ENDPOINT;
  getToken = null;
  extraHeaders = {};
  seen.clear();
  released.clear();
}

function listen(
  target: EventTarget,
  type: string,
  handler: (e: Event) => void,
): void {
  target.addEventListener(type, handler);
  teardown.push(() => target.removeEventListener(type, handler));
}

export function installReporter(options: ReporterOptions = {}): void {
  if (installed) return;
  installed = true;
  endpoint = options.endpoint ?? DEFAULT_ENDPOINT;
  maxPerSession = options.maxPerSession ?? DEFAULT_MAX_PER_SESSION;
  if (options.getToken) getToken = options.getToken;
  if (options.extraHeaders) extraHeaders = options.extraHeaders;

  listen(window, "error", (ev) => {
    const e = ev as ErrorEvent;
    reportError(e.error ?? e.message, {
      sourceUri: e.filename,
      line: e.lineno,
      column: e.colno,
    });
  });
  // The channel an ErrorBoundary CANNOT see: a rejected promise never passes
  // through React's render path.
  listen(window, "unhandledrejection", (ev) => {
    reportError((ev as PromiseRejectionEvent).reason);
  });
  // The channel neither of the two above can see: the browser REFUSING something
  // on policy grounds — nothing throws and nothing rejects, the resource simply
  // never exists. It cannot see a violation that fires BEFORE it installs, such as
  // a CSP blocking the main bundle itself.
  listen(document, "securitypolicyviolation", (ev) => {
    const e = ev as SecurityPolicyViolationEvent;
    const directive = e.effectiveDirective || e.violatedDirective;
    const err = new Error(
      `CSP blocked ${e.blockedURI || "a resource"} (${directive})`,
    );
    // The DOM event carries no stack, and the reporter's own frames would name
    // this file instead of the page.
    err.stack = "";
    reportError(err, {
      kind: "csp",
      directive,
      blockedUri: e.blockedURI,
      documentUri: e.documentURI,
      sourceUri: e.sourceFile,
      line: e.lineNumber,
      column: e.columnNumber,
    });
  });
}

const byteLength = (s: string): number => new TextEncoder().encode(s).length;

/**
 * The context keys that survive the context being shed. `kind` is the dimension
 * every CloudWatch query starts from: shedding it files a CSP violation as
 * report_kind="js", invisible to the one query that would find it. `directive` is
 * the other closed-vocabulary scalar, and `componentStack` is kept truncated (see
 * below); the unbounded caller-supplied fields are what shedding exists to drop.
 */
const KEPT_CONTEXT_KEYS = ["kind", "directive", "componentStack"] as const;

/** Bound on a kept field: these are scalars by contract, never a payload. */
const MAX_KEPT_FIELD_CHARS = 200;

/**
 * componentStack is not a scalar, so it gets its own larger bound instead of being
 * dropped. It is the ONLY thing the ErrorBoundary contributes, and a deep tree —
 * exactly what blows the budget and triggers shedding — is where it matters most:
 * a shortened React tree path is far more useful than none. Even at 4 bytes per
 * character this stays inside MAX_BODY_BYTES, and the frame/message loops below
 * still enforce the overall cap.
 */
const MAX_KEPT_COMPONENT_STACK_CHARS = 1024;

const keptLimit = (key: string): number =>
  key === "componentStack"
    ? MAX_KEPT_COMPONENT_STACK_CHARS
    : MAX_KEPT_FIELD_CHARS;

/**
 * Replaces an oversized or unserializable context with the few cheap scalars worth
 * keeping. Only short primitives are kept, and reading the object is itself
 * guarded: a getter that throws must not cost us the whole report.
 */
function shedContext(
  context: Record<string, unknown>,
): Record<string, unknown> {
  const kept: Record<string, unknown> = {};
  try {
    for (const key of KEPT_CONTEXT_KEYS) {
      const value = context[key];
      if (typeof value === "string") {
        if (value) kept[key] = value.slice(0, keptLimit(key));
      } else if (typeof value === "number" || typeof value === "boolean") {
        kept[key] = value;
      }
    }
  } catch {
    return {};
  }
  return kept;
}

/**
 * Serializes a report so the result never exceeds MAX_BODY_BYTES. Measures encoded
 * bytes, not string length: non-ASCII characters are 2-4 bytes each. Sheds the least
 * valuable data first: an oversized caller-supplied context, then trailing stack
 * frames (the top frames name the culprit), then the message tail.
 */
function serializeCapped(
  message: string,
  stack: string[],
  context: Record<string, unknown>,
): string {
  const userAgent = navigator.userAgent;
  const build = (m: string, s: string[], ctx: Record<string, unknown>) =>
    // ctx FIRST: a caller key named message/stack/userAgent must not clobber the
    // error's own fields.
    JSON.stringify({ ...ctx, message: m, stack: s, userAgent });
  const fits = (body: string) => byteLength(body) <= MAX_BODY_BYTES;

  // Context is the only input with no natural bound, so it is shed first rather
  // than letting it force out the stack and message — and down to
  // KEPT_CONTEXT_KEYS rather than to {}, so the report still says what kind it is.
  let ctx = context;
  try {
    if (byteLength(JSON.stringify(context)) > MAX_BODY_BYTES / 2) {
      ctx = shedContext(context);
    }
  } catch {
    // circular or otherwise unserializable: a thin report beats no report
    ctx = shedContext(context);
  }

  let frames = stack;
  let body = build(message, frames, ctx);
  while (!fits(body) && frames.length > 0) {
    frames = frames.slice(0, -1);
    body = build(message, frames, ctx);
  }

  let m = message;
  while (!fits(body) && m.length > 0) {
    m = m.slice(0, Math.floor(m.length / 2));
    body = build(m, frames, ctx);
  }

  return body;
}

/** Never throws: a token reader that does must not take the page down with it. */
function currentToken(): string | null {
  try {
    return getToken?.() ?? null;
  } catch {
    return null;
  }
}

/**
 * One transport, always. navigator.sendBeacon cannot set request headers, so it
 * cannot carry Authorization and cannot reach an authenticated endpoint at all.
 * `keepalive` takes headers AND survives the document being torn down.
 */
function send(body: string, token: string, fingerprint: string): void {
  void fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...extraHeaders,
    },
    body,
    keepalive: true,
  })
    .then((res) => {
      if (res.status === 401) release(fingerprint);
    })
    .catch(() => {});
}

/**
 * Gives back the fingerprint and the quota slot a rejected send already spent.
 *
 * The token is read from a cache that a long idle session leaves stale, so the
 * first error after expiry goes out with a dead token and comes back 401 — after
 * reportError has marked the fingerprint seen and charged the quota. Without this
 * that error is deduped away for the rest of the session and never reported at
 * all. Reading the token also kicks off a refresh (AuthBridge), so the next
 * occurrence carries a live one.
 *
 * Once per fingerprint: the ceiling stays at two sends for the same error, so a
 * token that never recovers cannot spin.
 */
function release(fingerprint: string): void {
  if (released.has(fingerprint)) return;
  released.add(fingerprint);
  seen.delete(fingerprint);
  if (sent > 0) sent -= 1;
}

/**
 * A stack line that names a location rather than repeating the error's own
 * message: V8 writes "Error: msg" first and indents frames with "at ", while
 * Firefox and Safari write "fn@url:line:col".
 */
const FRAME_LINE = /^\s*(at\s|\S+@)/;

/**
 * The top stack frame — what tells two errors that share a message apart.
 *
 * "Cannot read properties of undefined" is the most common message in JavaScript,
 * and neither the ErrorBoundary (context is { componentStack }) nor
 * unhandledrejection (context is {}) supplies sourceUri or line. Without the frame
 * the fingerprint degenerates to the message alone, so the first such error in a
 * session silences every unrelated one after it.
 */
function topFrame(stack: string[]): string {
  return (stack.find((line) => FRAME_LINE.test(line)) ?? "").trim();
}

/** String() throws on a hostile toString; a placeholder keeps the report alive. */
function safeString(value: unknown): string {
  try {
    return String(value);
  } catch {
    return "[unstringifiable]";
  }
}

export function reportError(
  error: unknown,
  context: Record<string, unknown> = {},
): void {
  // Every failure here is swallowed: a reporter that throws turns a handled error
  // into an unhandled one.
  try {
    if (sent >= maxPerSession) return;

    // No token means no signed-in session and the report is dropped. Returns
    // BEFORE spending the quota or marking the fingerprint, so a crash loop on the
    // login screen cannot silence the rest of the session once a token exists.
    const token = currentToken();
    if (!token) return;

    const err = error instanceof Error ? error : new Error(safeString(error));
    const raw = err.stack ?? "";
    const stack = raw ? raw.split("\n").slice(0, MAX_STACK_FRAMES) : [];
    const fingerprint = `${err.message}|${safeString(context.sourceUri ?? "")}|${safeString(context.line ?? "")}|${topFrame(stack)}`;
    if (seen.has(fingerprint)) return;

    // Serialize BEFORE spending quota or marking the fingerprint seen: a failure
    // here must not burn the budget or dedupe away a retry.
    const body = serializeCapped(err.message, stack, context);
    seen.add(fingerprint);
    sent += 1;
    send(body, token, fingerprint);
  } catch {
    /* reporting must never break the page */
  }
}
